import axios from 'axios';
import { API_URL } from './ApiConfig';

// Enum para los roles de usuario
export enum UserRole {
  USER = 'USER',
  ARCHITECT = 'ARCHITECT',
  SUPERADMIN = 'ADMIN'
}

// Interfaz para los datos de usuario
export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  token: string;
  isAuthenticated: boolean;
  role: UserRole; // Añadimos el rol como parte de la interfaz User
}


// Interfaz para la respuesta de login
interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
    role: string;
  };
}

// Crear una instancia de Axios configurada
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar el token a las solicitudes
apiClient.interceptors.request.use(
  (config) => {
    const user = getCurrentUser();
    console.log('Request interceptor - URL:', config.url);
    console.log('Request interceptor - User found:', !!user);
    if (user?.token) {
      console.log('Request interceptor - Adding token');
      config.headers['Authorization'] = `Bearer ${user.token}`;
    } else {
      console.log('Request interceptor - No token found');
    }
    console.log('Request interceptor - Headers:', config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Decide si un error de respuesta corresponde a una sesión caducada.
// Se marca 'sessionExpired' (para que el login lo avise) y se hace logout solo
// cuando es un 401 que NO viene de /auth/logout ni de /auth/login
// (un 401 en login son credenciales incorrectas, no sesión caducada).
// Exportada para poder testearla de forma aislada.
export const handleAuthError = (error: { response?: { status?: number }; config?: { url?: string } }): void => {
  const failedUrl = error.config?.url || '';
  if (
    error.response?.status === 401 &&
    !failedUrl.includes('/auth/logout') &&
    !failedUrl.includes('/auth/login')
  ) {
    localStorage.setItem('sessionExpired', '1');
    logout();
  }
};

// Interceptor para manejar errores de autenticación (401)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    handleAuthError(error);
    return Promise.reject(error);
  }
);

// Función para iniciar sesión
export const login = async (email: string, password: string): Promise<User> => {
  try {

    // Paso 1: Autenticar y obtener token
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      username: email,
      password,
    });

    // Crear datos básicos del usuario con el token
    const userData: User = {
      id: response.data.user.id,
      email: response.data.user.username,
      firstName: response.data.user.first_name,
      lastName: response.data.user.last_name,
      token: response.data.access_token,
      isAuthenticated: true,
      role: response.data.user.role as UserRole,
    };

    localStorage.setItem('user', JSON.stringify(userData));
    
    return userData;
  } catch (error) {
    // Limpiar localStorage en caso de error
    localStorage.removeItem('user');
    console.error('Error durante el login:', error);
    throw error;
  }
};

// Función para cerrar sesión
export const logout = async (): Promise<void> => {
  // Llamar al backend para invalidar el token antes de limpiar localStorage
  try {
    // El token se incluirá automáticamente gracias al interceptor
    await apiClient.post('/auth/logout');
  } catch (error) {
    console.error('Error al cerrar sesión en el backend:', error);
  } finally {
    // Siempre limpiar el localStorage, incluso si falla la petición
    localStorage.removeItem('user');
  }
};

// Función para obtener el usuario actual
export const getCurrentUser = (): User | null => {
  // Intentar obtener usuario del localStorage
  const userJson = localStorage.getItem('user');
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson) as User;
  } catch (error) {
    console.error('Error al parsear el usuario del localStorage:', error);
    return null;
  }
};

// Función para verificar si el token sigue siendo válido
export const verifyToken = async (): Promise<boolean> => {
  const user = getCurrentUser();
  if (!user) return false;

  try {
    // Endpoint que verifique si el token es válido
    await apiClient.get('/auth/verify');
    return true;
  } catch (error) {
    console.error('Error verificando token:', error);
    // Solo hacer logout si es un error 401 (no autorizado)
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      logout();
      return false;
    }
    // Para otros errores (como 404 si el endpoint no existe), 
    // asumimos que el token es válido si existe
    return true;
  }
};

// Verificar si un usuario tiene al menos el rol mínimo requerido
export const hasRole = (user: User | null, minimumRole: UserRole): boolean => {
  if (!user) return false;
  
  // Ordenamos los roles por nivel de acceso
  const roleHierarchy = [UserRole.USER, UserRole.ARCHITECT, UserRole.SUPERADMIN];
  
  const userRoleIndex = roleHierarchy.indexOf(user.role);
  const requiredRoleIndex = roleHierarchy.indexOf(minimumRole);
  
  // El usuario tiene acceso si su rol tiene un índice mayor o igual al requerido
  return userRoleIndex >= requiredRoleIndex;
};

// Función para obtener el nombre completo del usuario
export const getUserFullName = (user: User | null): string => {
  if (!user) return '';
  
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  
  return user.firstName || user.lastName || user.email;
};

// Función para obtener la URL del dashboard según el rol del usuario
export const getRoleDashboardUrl = (role: UserRole): string => {
  switch (role) {
    case UserRole.SUPERADMIN:
      return '/admin/dashboard';
    case UserRole.ARCHITECT:
      return '/architect/dashboard';
    case UserRole.USER:
    default:
      return '/user/dashboard';
  }
};

// Exportar el cliente de API configurado para usarlo en otros servicios
export default apiClient;